import type { AppSupabaseClient } from '@/infrastructure/supabase/client'
import type { SupabaseClient } from '@supabase/supabase-js'
import { z } from 'zod'
import { reportUnexpectedError } from '@/shared/infrastructure/observability/reportUnexpectedError'
import { phoneSearchPattern, splitSearchTokens } from '@/shared/domain/searchTokens'
import { findLeadIdsBySearch } from '@/infrastructure/supabase/partySearch'
import type { LeadRepository } from '../../application/LeadRepository'
import type { LeadLinkedQuote, LeadListFilters, LeadRecord, LeadStats, LeadStatus } from '../../domain/lead'
import { legacyLeadRowSchema, mapLegacyLead } from './legacyLeadMapper'
import { leadAttributionSchema } from '../../domain/leadAttribution'

const createLeadResultSchema = z.object({
  leadId: z.string(),
  number: z.string(),
  displayName: z.string(),
})

const leadColumns =
  'id,numero,prenom,nom,telephone,courriel,adresse,ville,code_postal,latitude,longitude,type_service,message,source,statut,statut_v3,statut_v4,version,lost_reason,lost_note,assigne_a,rappel_le,rappel_note,created_at,updated_at,deleted_at,converted_client:clients!clients_source_lead_id_fkey(id,numero),reminder:lead_reminders(id,kind,level,due_at,message,status)'

/** Seuls les statuts possédant une correspondance legacy sont filtrables. */
const legacyStatus: Partial<Record<LeadStatus, string>> = {
  NEW: 'nouveau',
  CONTACTED: 'contacte',
  QUOTE_SENT: 'soumission_envoyee',
  WON: 'converti',
  LOST: 'perdu',
}

const updateAddressResultSchema = z.object({
  leadId: z.uuid(),
  version: z.number().int().positive(),
  latitude: z.number(),
  longitude: z.number(),
})

const changeStatusResultSchema = z.object({
  leadId: z.uuid(),
  number: z.string().nullable(),
  status: z.enum([
    'NEW',
    'CONTACTED',
    'QUALIFIED',
    'QUOTE_PREPARED',
    'QUOTE_SENT',
    'WON',
    'LOST',
    'ARCHIVED',
  ]),
  legacyStatus: z.string(),
  version: z.number().int().positive(),
  changedAt: z.string(),
})

const callOutcomeResultSchema = z.object({
  leadId: z.uuid(),
  number: z.string().nullable(),
  statusV4: z.enum([
    'NEW',
    'TO_CONTACT',
    'CALLBACK',
    'NO_ANSWER',
    'CONTACTED_NOT_INTERESTED',
    'INTERESTED',
    'CONVERTED',
    'ARCHIVED',
  ]),
  version: z.number().int().positive(),
  changedAt: z.string(),
})

const duplicateSuggestionSchema = z.object({
  clientId: z.uuid(),
  number: z.string().nullable(),
  displayName: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  matchedOn: z.enum(['PHONE', 'EMAIL', 'ADDRESS']),
})

const convertResultSchema = z.object({
  leadId: z.uuid(),
  leadStatus: z.enum([
    'NEW',
    'CONTACTED',
    'QUALIFIED',
    'QUOTE_PREPARED',
    'QUOTE_SENT',
    'WON',
    'LOST',
    'ARCHIVED',
  ]),
  leadVersion: z.number().int().positive(),
  clientId: z.uuid(),
  clientNumber: z.string().nullable(),
  created: z.boolean(),
  linkedQuotes: z.number().int().nonnegative(),
})

const linkedQuoteRowSchema = z.object({
  id: z.string(),
  numero: z.string().nullable(),
  statut: z.string(),
  total: z.coerce.number(),
  updated_at: z.string(),
})

interface SupabaseResponse {
  data: unknown
  error: { message: string; code?: string } | null
  count: number | null
}

function asResponse(value: unknown): SupabaseResponse {
  return value as SupabaseResponse
}

function sanitizeSearch(value: string): string {
  return value
    .replace(/[%_,().'"\\]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80)
}

const leadStatsRowSchema = z.object({
  total: z.number(),
  new: z.number(),
  contacted: z.number(),
  quote_sent: z.number(),
  won: z.number(),
  lost: z.number(),
})

/** Les compteurs en une requête (`lead_list_stats_v1`), mêmes règles que les filtres de la liste. */
async function loadStats(client: AppSupabaseClient): Promise<LeadStats> {
  const rpcClient = client as unknown as SupabaseClient
  const response = asResponse(await rpcClient.rpc('lead_list_stats_v1'))
  if (response.error) throw new Error(`LEAD_COUNT_FAILED:${response.error.code ?? 'UNKNOWN'}`)
  const row: unknown = Array.isArray(response.data) ? response.data[0] : response.data
  const parsed = leadStatsRowSchema.safeParse(row)
  if (!parsed.success) throw new Error('LEAD_COUNT_RESPONSE_INVALID')
  const stats = parsed.data
  return {
    total: stats.total,
    new: stats.new,
    contacted: stats.contacted,
    quoteSent: stats.quote_sent,
    won: stats.won,
    lost: stats.lost,
  }
}

function mapRows(data: unknown): { leads: LeadRecord[]; anomalies: number } {
  if (!Array.isArray(data)) throw new Error('LEAD_LIST_RESPONSE_INVALID')
  const leads: LeadRecord[] = []
  let anomalies = 0
  for (const candidate of data) {
    const parsed = legacyLeadRowSchema.safeParse(candidate)
    if (!parsed.success) {
      anomalies += 1
      reportUnexpectedError(parsed.error, { feature: 'leads', operation: 'map-list-row' })
      continue
    }
    try {
      const lead = mapLegacyLead(parsed.data)
      leads.push(lead)
      // Sans téléphone ni courriel, le Lead ne peut pas être recontacté.
      if (!lead.contact.phone && !lead.contact.email) anomalies += 1
    } catch (error) {
      anomalies += 1
      reportUnexpectedError(error, { feature: 'leads', operation: 'map-list-row' })
    }
  }
  return { leads, anomalies }
}

async function loadLinkedQuotes(
  client: AppSupabaseClient,
  leadId: string,
): Promise<{ quotes: LeadLinkedQuote[]; anomalies: number }> {
  const response = asResponse(
    await client
      .from('quotes')
      .select('id,numero,statut,total,updated_at')
      .eq('lead_id', leadId)
      .is('deleted_at', null)
      .order('updated_at', { ascending: false }),
  )
  // Une Soumission illisible sous RLS ne doit pas rendre la fiche indisponible.
  if (response.error) return { quotes: [], anomalies: 1 }
  const parsed = z.array(linkedQuoteRowSchema).safeParse(response.data)
  if (!parsed.success) return { quotes: [], anomalies: 1 }
  const quotes: LeadLinkedQuote[] = []
  let anomalies = 0
  for (const row of parsed.data) {
    if (!row.numero) {
      anomalies += 1
      continue
    }
    quotes.push({
      id: row.id,
      number: row.numero,
      status: row.statut,
      totalCents: Math.round(row.total * 100),
      updatedAt: row.updated_at,
    })
  }
  return { quotes, anomalies }
}

export function createSupabaseLeadRepository(client: AppSupabaseClient): LeadRepository {
  return {
    async list(filters: LeadListFilters) {
      const from = (filters.page - 1) * filters.pageSize
      const to = from + filters.pageSize - 1
      let query = client
        .from('leads')
        .select(leadColumns, { count: 'exact' })
        .is('deleted_at', null)
        .order('updated_at', { ascending: false })
        .range(from, to)
      if (filters.status) {
        const legacy = legacyStatus[filters.status]
        // Un statut V3 sans équivalent legacy ne peut retourner aucune ligne.
        if (!legacy) {
          return {
            items: [],
            page: filters.page,
            pageSize: filters.pageSize,
            total: 0,
            stats: await loadStats(client),
            meta: { source: 'supabase', retrievedAt: new Date().toISOString(), anomalies: 0 },
          }
        }
        query = query.eq('statut', legacy)
      }
      const search = filters.search ? sanitizeSearch(filters.search) : ''
      if (search) {
        // Même règle que les Clients : un mot par groupe `or`, les groupes se
        // combinant par `and`. Sans cela « Patrick Goulet » ne retrouve rien,
        // le prénom et le nom vivant dans deux colonnes distinctes.
        // Les Leads retrouvés sans accents satisfont tous les mots.
        const tokens = splitSearchTokens(search)
        const byAccentless = await findLeadIdsBySearch(client, tokens)
        const accentless = byAccentless.length > 0 ? `,id.in.(${byAccentless.join(',')})` : ''
        for (const token of tokens) {
          query = query.or(
            `numero.ilike.%${token}%,prenom.ilike.%${token}%,nom.ilike.%${token}%,courriel.ilike.%${token}%,telephone.ilike.${phoneSearchPattern(token)}${accentless}`,
          )
        }
      }
      const [rawResponse, stats] = await Promise.all([query, loadStats(client)])
      const response = asResponse(rawResponse)
      if (response.error) throw new Error(`LEAD_LIST_FAILED:${response.error.code ?? 'UNKNOWN'}`)
      const mapped = mapRows(response.data)
      return {
        items: mapped.leads,
        page: filters.page,
        pageSize: filters.pageSize,
        total: response.count ?? mapped.leads.length,
        stats,
        meta: { source: 'supabase', retrievedAt: new Date().toISOString(), anomalies: mapped.anomalies },
      }
    },
    async getByReference(reference) {
      const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        reference,
      )
      let query = client.from('leads').select(leadColumns).is('deleted_at', null)
      query = uuid ? query.eq('id', reference) : query.eq('numero', reference)
      const response = asResponse(await query.maybeSingle())
      if (response.error) throw new Error(`LEAD_DETAIL_FAILED:${response.error.code ?? 'UNKNOWN'}`)
      if (response.data === null) return null
      const parsed = legacyLeadRowSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('LEAD_DETAIL_RESPONSE_INVALID')
      const lead = mapLegacyLead(parsed.data)
      const linked = await loadLinkedQuotes(client, lead.id)
      return {
        lead,
        quotes: linked.quotes,
        meta: {
          source: 'supabase',
          retrievedAt: new Date().toISOString(),
          anomalies: linked.anomalies + ((lead.contact.phone ?? lead.contact.email) ? 0 : 1),
        },
      }
    },
    async create(command) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(
        await rpcClient.rpc('create_lead_v1', {
          p_command: Object.fromEntries(
            Object.entries(command).filter(([, value]) => value !== undefined && value !== ''),
          ),
        }),
      )
      if (response.error) {
        throw new Error(response.error.message || `LEAD_CREATE_FAILED:${response.error.code ?? 'UNKNOWN'}`)
      }
      const parsed = createLeadResultSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('LEAD_CREATE_RESPONSE_INVALID')
      return parsed.data
    },
    async changeStatus(command) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(
        await rpcClient.rpc('change_lead_status_v1', {
          p_command: {
            idempotencyKey: command.idempotencyKey,
            leadId: command.leadId,
            expectedVersion: command.expectedVersion,
            toStatus: command.toStatus,
            ...(command.reason ? { reason: command.reason } : {}),
            ...(command.note ? { note: command.note } : {}),
          },
        }),
      )
      if (response.error) {
        throw new Error(response.error.message || `LEAD_STATUS_FAILED:${response.error.code ?? 'UNKNOWN'}`)
      }
      const parsed = changeStatusResultSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('LEAD_STATUS_RESPONSE_INVALID')
      return parsed.data
    },
    async updateAddress(command) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(
        await rpcClient.rpc('update_lead_address_v1', {
          p_command: {
            idempotencyKey: command.idempotencyKey,
            leadId: command.leadId,
            expectedVersion: command.expectedVersion,
            latitude: command.latitude,
            longitude: command.longitude,
            ...(command.address ? { address: command.address } : {}),
            ...(command.city ? { city: command.city } : {}),
            ...(command.postalCode ? { postalCode: command.postalCode } : {}),
          },
        }),
      )
      if (response.error) {
        throw new Error(response.error.message || `LEAD_ADDRESS_FAILED:${response.error.code ?? 'UNKNOWN'}`)
      }
      const parsed = updateAddressResultSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('LEAD_ADDRESS_RESPONSE_INVALID')
      return parsed.data
    },
    async findDuplicates(leadId) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(await rpcClient.rpc('find_client_duplicates_v1', { p_lead_id: leadId }))
      if (response.error) throw new Error(`LEAD_DUPLICATES_FAILED:${response.error.code ?? 'UNKNOWN'}`)
      const parsed = z.array(duplicateSuggestionSchema).safeParse(response.data)
      if (!parsed.success) throw new Error('LEAD_DUPLICATES_RESPONSE_INVALID')
      return parsed.data
    },
    async convert(command) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(
        await rpcClient.rpc('convert_lead_to_client_v1', {
          p_command: {
            idempotencyKey: command.idempotencyKey,
            leadId: command.leadId,
            expectedVersion: command.expectedVersion,
            mode: command.mode,
            ...(command.clientId ? { clientId: command.clientId } : {}),
            ...(command.acknowledgeDuplicates ? { acknowledgeDuplicates: true } : {}),
            ...(command.clientType ? { clientType: command.clientType } : {}),
            ...(command.phone ? { phone: command.phone } : {}),
          },
        }),
      )
      if (response.error) {
        throw new Error(
          response.error.message || `LEAD_CONVERSION_FAILED:${response.error.code ?? 'UNKNOWN'}`,
        )
      }
      const parsed = convertResultSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('LEAD_CONVERSION_RESPONSE_INVALID')
      return parsed.data
    },

    async completeReminder(command) {
      const rpcClient = client as unknown as {
        rpc: (name: string, args: Record<string, unknown>) => Promise<unknown>
      }
      const response = asResponse(
        await rpcClient.rpc('complete_lead_reminder_v1', {
          p_command: {
            reminderId: command.reminderId,
            ...(command.note ? { note: command.note } : {}),
          },
        }),
      )
      if (response.error) {
        throw new Error(response.error.message || 'LEAD_REMINDER_COMPLETION_FAILED')
      }
    },

    async recordCallOutcome(command) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(
        await rpcClient.rpc('record_lead_call_outcome_v1', {
          p_command: {
            idempotencyKey: command.idempotencyKey,
            leadId: command.leadId,
            expectedVersion: command.expectedVersion,
            outcome: command.outcome,
            ...(command.reason ? { reason: command.reason } : {}),
            ...(command.note ? { note: command.note } : {}),
            ...(command.callbackAt ? { callbackAt: command.callbackAt } : {}),
          },
        }),
      )
      if (response.error) {
        throw new Error(
          response.error.message || `LEAD_CALL_OUTCOME_FAILED:${response.error.code ?? 'UNKNOWN'}`,
        )
      }
      const parsed = callOutcomeResultSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('LEAD_CALL_OUTCOME_RESPONSE_INVALID')
      return parsed.data
    },

    async delete(leadId) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(
        await rpcClient.rpc('delete_lead_v1', {
          p_command: { leadId },
        }),
      )
      if (response.error) {
        throw new Error(response.error.message || 'LEAD_DELETE_FAILED')
      }
    },

    async getAttribution(leadId) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(await rpcClient.rpc('lead_attribution_v1', { p_lead_id: leadId }))
      if (response.error) throw new Error(response.error.message || 'LEAD_ATTRIBUTION_FAILED')
      if (response.data === null) return null
      const parsed = leadAttributionSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('LEAD_ATTRIBUTION_RESPONSE_INVALID')
      return parsed.data
    },

    async setDeclaredSource(command) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(
        await rpcClient.rpc('set_lead_declared_source_v1', {
          p_command: Object.fromEntries(Object.entries(command).filter(([, value]) => value !== undefined)),
        }),
      )
      if (response.error) throw new Error(response.error.message || 'LEAD_ATTRIBUTION_FAILED')
      const parsed = leadAttributionSchema.nullable().safeParse(response.data)
      if (!parsed.success) throw new Error('LEAD_ATTRIBUTION_RESPONSE_INVALID')
      return parsed.data
    },

    async geocodeAddress(command) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(await rpcClient.rpc('geocode_lead_address_v1', { p_command: command }))
      if (response.error) throw new Error(response.error.message || 'LEAD_GEOCODE_FAILED')
    },
  }
}
